import { createSelector } from 'reselect';
import produce from 'immer';
import moment from 'moment';
import get from 'lodash.get';
import { create } from 'services/api';
import { ADD_DRAFT, ADD_SHOW_DRAFT, RECEIVE_SHOW_EPISODES } from './shared-constants';
import { addOneToImmerState, addManyToImmerState } from './util';

const UNPUBLISH = 'create/episode/UNPUBLISH';
const UPDATE = 'create/episode/UPDATE';

export function getEpisodeStatus(episode) {
  if (episode.published === null) {
    return 'Draft';
  }
  if (episode.publishDate !== null && moment(episode.publishDate).isBefore(new Date())) {
    return 'Published';
  }
  if (episode.publishDate !== null && moment(episode.publishDate).isAfter(new Date())) {
    return 'Scheduled';
  }
  return 'Unknown';
}

export function hasImage(episode) {
  return !!(episode.image && episode.image.original && episode.image.original !== '');
}

export const selectEpisodes = state => get(state, ['v2', 'episodes', 'items'], null);
export const selectEpisode = episodeId =>
  createSelector(
    selectEpisodes,
    episodes => episodes && episodes[episodeId]
  );
export const selectEpisodeStatus = episodeId =>
  createSelector(
    selectEpisodes,
    episodes => {
      const episode = episodes[episodeId];
      return getEpisodeStatus(episode);
    }
  );

const initialState = {
  total: null,
  items: {},
};
/* eslint-disable no-param-reassign, default-case, consistent-return */
export default produce((state, action) => {
  switch (action.type) {
    case RECEIVE_SHOW_EPISODES:
      addManyToImmerState(state, action.payload);
      break;
    case UPDATE:
      addOneToImmerState(state, action.payload);
      break;
    case UNPUBLISH:
      delete state.items[action.payload];
      state.total -= 1;
      break;
  }
}, initialState);
/* eslint-enable no-param-reassign, default-case, consistent-return */

export const updateEpisode = episode => dispatch => {
  if (!episode || !episode.id) {
    return Promise.resolve();
  }

  return create.patch(`/api-v2/acasts/${episode.id}`, episode).then(response => {
    dispatch({ type: UPDATE, payload: response.body });
    return Promise.resolve();
  });
};

export const resetEpisodeConditions = episode => dispatch => {
  // If we don't have any conditions there's nothing to reset
  if (!episode || !episode.conditions || !episode.conditions.length) {
    return Promise.resolve();
  }

  return updateEpisode({
    ...episode,
    conditions: [],
  })(dispatch);
};

export const unpublishEpisode = episode => dispatch => {
  return create
    .post(`/api-v2/acasts/${episode.id}/unpublish`, episode)
    .then(() => resetEpisodeConditions(episode)(dispatch))
    .then(() => {
      dispatch({ type: UNPUBLISH, payload: episode.id });

      // Reset publish date and acast+ conditions locally.
      const draft = {
        ...episode,
        published: null,
        publishDate: null,
        conditions: [],
      };
      dispatch({ type: ADD_SHOW_DRAFT, payload: { showId: draft.channel, draftId: draft.id } });
      dispatch({ type: ADD_DRAFT, payload: draft });
      return draft;
    });
};
